import { useState, useCallback, useEffect } from 'react';
import { isValidJSONObject } from '@ion/mrdash';
import { ERRORS } from './errors';
import { debounce as _debounce } from 'lodash';
import useTrackWithUser from '@ion/api/hooks/useTrackWithUser';

export default function useInputEventEditor({ setValue, setHasValidInput, trackingEventName }) {
  const [errorMessage, setErrorMessage] = useState(null);
  const [trackEditInput] = useTrackWithUser(trackingEventName);

  const clearErrorMessage = useCallback(() => setErrorMessage(null), [setErrorMessage]);

  function validateAndSetInput(value) {
    setValue(value);
    if (!value) {
      setHasValidInput(false);
      setErrorMessage(null);
    } else if (isValidJSONObject(value)) {
      setHasValidInput(true);
      setErrorMessage(null);
    } else {
      setHasValidInput(false);
      setErrorMessage(ERRORS.INVALID_JSON);
    }
  }

  function onSetInput(event) {
    const code = event.target.value;
    validateAndSetInput(code);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnSetInput = useCallback(
    _debounce(event => {
      onSetInput(event);
      trackEditInput();
    }, 1200),
    []
  );

  const onBlur = event => {
    debouncedOnSetInput.cancel();
    onSetInput(event);
  };

  const onChange = event => {
    if (errorMessage) {
      clearErrorMessage();
    }
    debouncedOnSetInput(event);
  };

  useEffect(() => {
    return () => {
      debouncedOnSetInput.cancel();
    };
  }, [debouncedOnSetInput]);

  return { errorMessage, onChange, onBlur };
}
